import React, { useState } from "react";
import SectionTitle from "../components/SectionTitle";
import Seo from "../components/Seo";
import { useData } from "../context/DataContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Users, Calendar as CalendarIcon, X, Loader2, CheckCircle2 } from "lucide-react";

const fmtEur = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} €`;

const BookingModal = ({ stage, applicationFee, onClose }) => {
  const [form, setForm] = useState({
    parentName: "", email: "", phone: "", postalCode: "",
    childFirstName: "", childLastName: "", childAge: "",
    paymentMode: "cheque", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const total = Number(stage?.price || 0) + Number(applicationFee || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.parentName || !form.email || !form.childFirstName) {
      toast.error("Merci de renseigner les champs obligatoires."); return;
    }
    setSubmitting(true);
    try {
      await api.post("/stages/book", { ...form, stageId: stage.id });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur lors de la réservation.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-900/70 backdrop-blur-sm flex items-start justify-center p-4 sm:p-6 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl shadow-xl max-w-lg w-full my-8 p-6 sm:p-8 relative" data-testid="stage-booking-modal">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-600"><X size={18}/></button>
        {done ? (
          <div className="text-center py-6" data-testid="stage-booking-done">
            <CheckCircle2 size={56} className="mx-auto text-emerald-500" />
            <h3 className="font-serif-display text-2xl text-stone-800 mt-5">Réservation enregistrée&nbsp;!</h3>
            <p className="text-stone-600 mt-3">Vous recevrez un email de confirmation. Le règlement se fait en {form.paymentMode === "cheque" ? "chèque" : form.paymentMode} à la première séance ou en amont.</p>
            <button onClick={onClose} className="mt-6 inline-flex items-center gap-2 bg-coral text-white px-6 py-2.5 rounded-full text-sm tracking-widest uppercase">Fermer</button>
          </div>
        ) : (
          <>
            <div className="text-xs uppercase tracking-widest text-deep-rose">Réservation de stage</div>
            <h3 className="font-serif-display text-2xl text-stone-800 mt-1">{stage.title}</h3>
            <p className="text-stone-500 text-sm mt-1">{stage.dates}</p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <span className="bg-blush-light text-deep-rose px-3 py-1 rounded-full">{fmtEur(stage.price)}</span>
              {applicationFee > 0 && <span className="bg-stone-100 text-stone-600 px-3 py-1 rounded-full">+ frais dossier {fmtEur(applicationFee)}</span>}
              <span className="bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full">Total : {fmtEur(total)}</span>
            </div>

            <form onSubmit={submit} className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3" autoComplete="on">
              <input required placeholder="Votre nom *" value={form.parentName} onChange={update("parentName")} data-testid="stage-parent-name" className="sm:col-span-2 bg-cream border border-blush-light rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush"/>
              <input required type="email" placeholder="Email *" value={form.email} onChange={update("email")} data-testid="stage-email" className="bg-cream border border-blush-light rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blush"/>
              <input placeholder="Téléphone" value={form.phone} onChange={update("phone")} className="bg-cream border border-blush-light rounded-xl px-3 py-2 text-sm"/>
              <input placeholder="Code postal" value={form.postalCode} onChange={update("postalCode")} inputMode="numeric" pattern="[0-9]{5}" maxLength={5} className="bg-cream border border-blush-light rounded-xl px-3 py-2 text-sm"/>
              <input required placeholder="Prénom de l'enfant *" value={form.childFirstName} onChange={update("childFirstName")} data-testid="stage-child-first" className="bg-cream border border-blush-light rounded-xl px-3 py-2 text-sm"/>
              <input placeholder="Nom de l'enfant" value={form.childLastName} onChange={update("childLastName")} className="bg-cream border border-blush-light rounded-xl px-3 py-2 text-sm"/>
              <input placeholder="Âge" value={form.childAge} onChange={update("childAge")} inputMode="numeric" className="bg-cream border border-blush-light rounded-xl px-3 py-2 text-sm"/>
              <select value={form.paymentMode} onChange={update("paymentMode")} className="bg-cream border border-blush-light rounded-xl px-3 py-2 text-sm">
                <option value="cheque">Chèque</option>
                <option value="especes">Espèces</option>
              </select>
              <textarea rows={2} placeholder="Message (optionnel)" value={form.message} onChange={update("message")} className="sm:col-span-2 bg-cream border border-blush-light rounded-xl px-3 py-2 text-sm"/>
              <div className="sm:col-span-2 flex flex-col sm:flex-row gap-3 justify-end mt-2">
                <button type="button" onClick={onClose} className="px-5 py-2 rounded-full bg-stone-100 text-stone-700 text-sm">Annuler</button>
                <button type="submit" disabled={submitting} data-testid="stage-book-submit" className="px-6 py-2 rounded-full bg-coral text-white text-sm tracking-widest uppercase disabled:opacity-50 inline-flex items-center gap-2">
                  {submitting ? <><Loader2 className="animate-spin" size={14}/> Envoi...</> : "Réserver"}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

const Stages = () => {
  const { stages, settings, loaded } = useData();
  const [selected, setSelected] = useState(null);
  const applicationFee = Number(settings?.applicationFee || 0);

  return (
    <>
      <Seo title="Stages de vacances — L'Élan Danse" description="Réservez les stages de danse pour les vacances scolaires à L'Élan Danse au Perray-en-Yvelines." />

      <section className="py-16 sm:py-20 px-4 sm:px-6">
        <SectionTitle kicker="Vacances scolaires" title="Stages & ateliers" subtitle="Réservez un ou plusieurs stages ponctuels pendant les vacances. Ouvert aux danseurs déjà inscrits à l'école." />

        {!loaded ? (
          <div className="text-center text-stone-500">Chargement...</div>
        ) : stages.length === 0 ? (
          <div className="text-center text-stone-500 max-w-md mx-auto py-8">Aucun stage n&apos;est programmé pour le moment. Revenez bientôt !</div>
        ) : (
          <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-6">
            {stages.map((s) => (
              <article key={s.id} className="bg-white rounded-3xl overflow-hidden shadow-sm flex flex-col" data-testid={`stage-card-${s.id}`}>
                {s.image ? (
                  <div className="h-48 bg-blush-light">
                    <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="h-48 bg-gradient-to-br from-blush-light to-cream flex items-center justify-center text-deep-rose">
                    <Sparkles size={48} />
                  </div>
                )}
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="font-serif-display text-xl text-stone-800">{s.title}</h3>
                  <div className="flex items-center gap-3 text-xs text-stone-500 mt-1 flex-wrap">
                    {s.dates && <span className="inline-flex items-center gap-1"><CalendarIcon size={12}/>{s.dates}</span>}
                    {s.ageRange && <span>· {s.ageRange}</span>}
                    {s.teacherName && <span>· {s.teacherName}</span>}
                    {s.maxStudents > 0 && <span className="inline-flex items-center gap-1"><Users size={12}/>{s.maxStudents} places</span>}
                  </div>
                  {s.description && <p className="text-stone-600 text-sm mt-3 flex-1">{s.description}</p>}
                  <div className="mt-4 flex items-center justify-between gap-3">
                    <span className="font-serif-display text-2xl text-deep-rose">{fmtEur(s.price)}</span>
                    <button onClick={() => setSelected(s)} data-testid={`stage-book-btn-${s.id}`} className="bg-coral text-white px-5 py-2 rounded-full text-xs tracking-widest uppercase hover:bg-deep-rose transition-colors">Réserver</button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {selected && <BookingModal stage={selected} applicationFee={applicationFee} onClose={() => setSelected(null)} />}
    </>
  );
};

export default Stages;
